import { useEffect, useRef } from 'react'
import { Content } from '@phrasee/phrasee-typings'

import useWebsockets from 'common/hooks/useWebsockets'

/**
 * Subscribes to Generation Topics still listed as Pending Generation Markers.
 * Runs once per content for each WebSocket ready session (page load, and again
 * after reconnect) so Generation Ready Notifications can arrive.
 *
 * New markers created while already connected are subscribed at generation
 * start via sendWebsocketMessage — this hook must not subscribe those again.
 */
const usePendingGenerationMarkersSubscription = (
  content: Content | undefined
): void => {
  const { sendJsonMessage, isConnectionReady } = useWebsockets()
  const contentId = content?._id
  const pendingTopicsKey =
    content?.ws_messages?.map((message) => message.wsTopic).join('\0') ?? ''

  // Reset when the socket drops so reconnect re-subscribes. Set when we first
  // see this content while ready (even with zero markers) so optimistic marker
  // adds during an active session do not trigger another subscribe wave.
  const syncedContentIdForReadySessionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isConnectionReady) {
      syncedContentIdForReadySessionRef.current = null
      return
    }

    if (!contentId) {
      return
    }

    if (syncedContentIdForReadySessionRef.current === contentId) {
      return
    }

    syncedContentIdForReadySessionRef.current = contentId

    if (!pendingTopicsKey) {
      return
    }

    pendingTopicsKey.split('\0').forEach((wsTopic) => {
      sendJsonMessage({
        action: 'subscribe',
        topics: [wsTopic],
      })
    })
  }, [contentId, isConnectionReady, pendingTopicsKey, sendJsonMessage])
}

export default usePendingGenerationMarkersSubscription
